import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
    Search,
    Filter,
    Download,
    FileText,
    ArrowUpRight,
    Loader2,
    Upload,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/lib/supabaseClient";

const STATUS_BADGE = {
    Eligible: "bg-emerald-50 text-emerald-700 border-emerald-200",
    "Partially Eligible": "bg-amber-50 text-amber-700 border-amber-200",
    "Not Eligible": "bg-red-50 text-red-700 border-red-200",
    Completed: "bg-slate-50 text-slate-600 border-slate-200",
};

function toHistoryItem(r) {
    return {
        id: r.id,
        name: r.result?.name || r.file_name,
        department: r.result?.department || "—",
        date: new Date(r.created_at).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        }),
        value: r.result?.value || "—",
        matchScore: r.result?.match_score ?? null,
        status: r.result?.eligibility_status || "Completed",
    };
}

function matchesFilters(item, q, filter) {
    if (filter !== "all" && item.status.toLowerCase() !== filter) return false;
    if (!q) return true;
    const needle = q.toLowerCase();
    return (
        item.name.toLowerCase().includes(needle) ||
        item.department.toLowerCase().includes(needle)
    );
}

export default function AnalysisHistory() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("all");

    useEffect(() => {
        (async () => {
            const { data } = await supabase
                .from("tender_analyses")
                .select("*")
                .order("created_at", { ascending: false });
            setRows(data || []);
            setLoading(false);
        })();
    }, []);

    const filtered = rows
        .map(toHistoryItem)
        .filter((item) => matchesFilters(item, q, filter));

    return (
        <div className="space-y-6" data-testid="history-page">
            <div>
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
                    Analysis History
                </h1>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                    Every tender you&apos;ve analyzed — searchable and exportable.
                </p>
            </div>

            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
                <HistoryToolbar q={q} setQ={setQ} filter={filter} setFilter={setFilter} />

                {loading && (
                    <div className="p-16 flex items-center justify-center text-slate-500">
                        <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
                    </div>
                )}

                {!loading && rows.length === 0 && <EmptyHistory />}

                {!loading && rows.length > 0 && (
                    <HistoryTable filtered={filtered} />
                )}
            </div>
        </div>
    );
}

function HistoryToolbar({ q, setQ, filter, setFilter }) {
    return (
        <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search tender or department..."
                    className="pl-9 bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800"
                    data-testid="history-search-input"
                />
            </div>
            <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-full sm:w-48" data-testid="history-filter">
                    <Filter className="h-4 w-4 mr-2 text-slate-400" />
                    <SelectValue placeholder="Filter" />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="eligible">Eligible</SelectItem>
                    <SelectItem value="partially eligible">Partially Eligible</SelectItem>
                    <SelectItem value="not eligible">Not Eligible</SelectItem>
                </SelectContent>
            </Select>
            <Button variant="outline" className="border-slate-200 dark:border-slate-700">
                <Download className="h-4 w-4 mr-2" /> Export CSV
            </Button>
        </div>
    );
}

function EmptyHistory() {
    return (
        <div className="p-14 text-center" data-testid="history-empty">
            <FileText className="h-9 w-9 text-slate-400 mx-auto mb-3" />
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                No analyses yet
            </p>
            <p className="text-xs text-slate-500 mt-1">
                Upload your first tender PDF to see it here.
            </p>
            <Link to="/dashboard/upload" className="mt-5 inline-block">
                <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                    <Upload className="h-3.5 w-3.5 mr-1.5" /> Upload Tender
                </Button>
            </Link>
        </div>
    );
}

function HistoryTable({ filtered }) {
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead className="bg-slate-50/60 dark:bg-slate-950/40 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <tr>
                        <th className="px-5 py-3">Tender</th>
                        <th className="px-5 py-3 hidden md:table-cell">Department</th>
                        <th className="px-5 py-3 hidden sm:table-cell">Date</th>
                        <th className="px-5 py-3 hidden md:table-cell">Value</th>
                        <th className="px-5 py-3">Match</th>
                        <th className="px-5 py-3">Status</th>
                        <th className="px-5 py-3 w-12"></th>
                    </tr>
                </thead>
                <tbody>
                    {filtered.map((r) => (
                        <HistoryRow key={r.id} r={r} />
                    ))}
                </tbody>
            </table>
            {filtered.length === 0 && (
                <div className="p-12 text-center text-slate-500">
                    No analyses match your filters.
                </div>
            )}
        </div>
    );
}

function HistoryRow({ r }) {
    return (
        <tr
            className="border-t border-slate-100 dark:border-slate-800/60 hover:bg-slate-50/60 dark:hover:bg-slate-950/40 transition-colors"
            data-testid={`history-row-${r.id}`}
        >
            <td className="px-5 py-4">
                <div className="flex items-center gap-3 min-w-0">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-800 shrink-0">
                        <FileText className="h-4 w-4 text-slate-600 dark:text-slate-400" />
                    </div>
                    <div className="min-w-0">
                        <p className="font-medium text-slate-900 dark:text-slate-50 truncate max-w-[280px]">
                            {r.name}
                        </p>
                        <p className="text-xs text-slate-500 font-mono">#{r.id}</p>
                    </div>
                </div>
            </td>
            <td className="px-5 py-4 text-slate-600 dark:text-slate-400 hidden md:table-cell">
                {r.department}
            </td>
            <td className="px-5 py-4 text-slate-500 hidden sm:table-cell">{r.date}</td>
            <td className="px-5 py-4 font-mono text-slate-900 dark:text-slate-50 hidden md:table-cell">
                {r.value}
            </td>
            <td className="px-5 py-4">
                {r.matchScore === null ? (
                    <span className="text-xs text-slate-400">—</span>
                ) : (
                    <div className="flex items-center gap-2">
                        <div className="h-1.5 w-16 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-indigo-500 to-violet-500"
                                style={{ width: `${r.matchScore}%` }}
                            />
                        </div>
                        <span className="font-mono text-xs font-semibold text-slate-700 dark:text-slate-300">
                            {r.matchScore}
                        </span>
                    </div>
                )}
            </td>
            <td className="px-5 py-4">
                <Badge
                    className={`${STATUS_BADGE[r.status] || STATUS_BADGE.Completed} border`}
                    variant="outline"
                >
                    {r.status}
                </Badge>
            </td>
            <td className="px-5 py-4">
                <Link
                    to={`/dashboard/analysis/${r.id}`}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-indigo-600"
                    data-testid={`history-view-${r.id}`}
                >
                    <ArrowUpRight className="h-4 w-4" />
                </Link>
            </td>
        </tr>
    );
}
